import { CarrierVisitStatus, VesselType } from '@planning/app/api'
import { IVesselVisitItem } from '@planning/rt-stores/vesselVisit/VesselVisitItem'
import { VesselVisitItemStore } from '@planning/rt-stores/vesselVisit/VesselVisitItemStore'
import _ from 'lodash'
import { action, computed, makeObservable, observable } from 'mobx'
import { ViewType } from '../Components/atoms/ViewToggleButtons'

export interface VesselVisitTableDto {
  id: number
  number?: string | null
  identifier: string
  vesselLength: number | null
  operatorName: string | null
  agentName: string | null
  importVoyage: string | null
  exportVoyage: string | null
  portCallId: string | null
  eta: string | null
  ata: string | null
  etb: string | null
  atb: string | null
  etd: string | null
  atd: string | null
  berthNames: string
  moves: number
  status: CarrierVisitStatus

  cargoType?: string
  discharge?: number | null
  load?: number | null
  restow?: number | null
  dockingSide?: string
  startOfOperations?: string | null
  endOfOperations?: string | null
  notes?: string | null
  orderIds?: number[]
  vesselType: VesselType
}

export class VesselVisitsViewStore {
  viewType: ViewType = 'list'
  isLoading = false

  constructor(private readonly vesselVisitItemStore: VesselVisitItemStore) {
    makeObservable(this, {
      viewType: observable,

      vesselVisitTableDtos: computed,

      fetchVisits: action,
      setViewType: action,
    })
  }

  async fetchVisits() {
    this.isLoading = true

    try {
      await this.vesselVisitItemStore.fetchAll()
    } catch (e) {
      console.error('Failed to fetch vessel visits', e)
    } finally {
      this.isLoading = false
    }
  }

  setViewType = (type: ViewType) => {
    this.viewType = type
  }

  get vesselVisitTableDtos(): VesselVisitTableDto[] {
    return _.values(this.vesselVisitItemStore.elements).map(this.mapVesselVisitItemToTableDto)
  }

  mapVesselVisitItemToTableDto = (item: IVesselVisitItem): VesselVisitTableDto => {
    const vesselLength = item.vessels[0]?.data.length ?? '-'
    const vesselType = item.vessels[0]?.data.vesselType ?? '-'

    const berthNames =
      item.berths
        .map(b => b.name)
        .sort((a, b) => a.localeCompare(b))
        .join(', ') || '-'

    const operatorName = item.operator?.data.name ?? '-'
    const agentName = item.shippingAgent?.data.name ?? '-'

    const importVoyage = item.voyages.map(i => i.inboundNumber).join(', ') || '-'
    const exportVoyage = item.voyages.map(i => i.outboundNumber).join(', ') || '-'
    const portCallId = item.data.portCallId ?? null

    const eta = item.data.eta ?? null
    const ata = item.data.ata ?? null
    const etb = item.data.etb ?? null
    const atb = item.data.atb ?? null
    const etd = item.data.etd ?? null
    const atd = item.data.atd ?? null

    const moves =
      (item.data.dischargeEstimate ?? 0) +
      (item.data.loadEstimate ?? 0) +
      (item.data.restowEstimate ?? 0)

    return {
      id: item.data.id,
      number: item.data.number ?? '-',
      identifier: item.data.identifier ?? '-',
      vesselLength,
      operatorName,
      agentName,
      importVoyage,
      exportVoyage,
      portCallId,
      eta,
      ata,
      etb,
      atb,
      etd,
      atd,
      berthNames,
      moves: moves,
      status: item.data.status,
      cargoType: item.data.cargoType,
      discharge: item.data.dischargeEstimate ?? 0,
      load: item.data.loadEstimate ?? 0,
      restow: item.data.restowEstimate ?? 0,
      dockingSide: item.data.berthSide,
      startOfOperations: item.data.operationStart ?? null,
      endOfOperations: item.data.operationEnd ?? null,
      notes: item.data.notes,
      orderIds: item.data.orderIds,
      vesselType,
    }
  }
}
